import React from 'react'
import { Button, CircularProgress, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import DailyUsageGraph, { DailyUsageYLabelType } from '../Graphs/DailyUsageGraph'
import { i18nMessages } from '../../Logic/CodeAccessible.i18n.Messages'
import { CapeeshColors } from '../../assets/ColorPalette'

const useStyles = makeStyles(() => ({
    panel: {
        marginTop: '20px',
        padding: '20px',
        backgroundColor: CapeeshColors.OffWhite,
        borderRadius: '10px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
    },
    title: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '18px',
    },
    sectionTitle: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '14px',
        marginTop: '20px',
    },
    caption: {
        fontFamily: 'Rubik',
        fontSize: '12px',
        color: CapeeshColors.Gray1,
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: '30px',
    },
}))

/**
 * Per topic detail. Loaded on demand for the selected topic only - building a trend for every row
 * of the overview would turn one request into hundreds.
 *
 * The `course` prop carries an API row, so its fields keep the backend spelling: topicName is the
 * topic shown in the heading, courseGroupName is the course it belongs to.
 *
 * The score distribution is a full shape rather than a single cutoff on purpose: it shows a course
 * skewing low without the page committing to anyone's pass threshold. Naming the people in the low
 * buckets is the filter panel's job.
 */
const CourseStatsDetailPanel = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const { course, trend, loading } = props

    if (!course) {
        return null
    }

    const graphData = (trend?.days || []).map((day) => ({
        name: day.date,
        value: day.lessonsPassed,
    }))

    const distributionData = (trend?.scoreDistribution || []).map((bucket) => ({
        name: `${bucket.lowerPercentInclusive}-${bucket.upperPercentExclusive}%`,
        users: bucket.numberOfUsers,
    }))

    return (
        <div className={classes.panel}>
            <div className={classes.header}>
                <div>
                    <Typography className={classes.title}>{course.topicName}</Typography>
                    <Typography className={classes.caption}>{course.courseGroupName}</Typography>
                </div>
                {/* Absent for organizations that do not permit individual statistics - the caller
                    passes no handler rather than the button being disabled, since a disabled
                    control invites people to ask why. */}
                {props.onFindUsersInTopic && (
                    <Button variant="outlined" color="primary" onClick={() => props.onFindUsersInTopic(course)}>
                        {intl.formatMessage(i18nMessages.courseStatisticsFindUsersInTopic)}
                    </Button>
                )}
            </div>

            {loading && (
                <div className={classes.loading}>
                    <CircularProgress size={28} />
                </div>
            )}

            {!loading && trend && (
                <div>
                    <Typography className={classes.sectionTitle}>
                        {intl.formatMessage(i18nMessages.courseStatisticsTrendTitle)}
                    </Typography>
                    <DailyUsageGraph
                        graphData={graphData}
                        canShowChart={graphData.length > 0}
                        showReferenceLine={false}
                        toolTipYLabelType={DailyUsageYLabelType.NumberOfLessons}
                        title={
                            <FormattedMessage
                                id="globalwords.coursestatistics.trend.lessonspassed"
                                defaultMessage="Lessons passed per day"
                            />
                        }
                        defaultHeaderMessage={
                            <FormattedMessage
                                id="globalwords.coursestatistics.trend.nodata"
                                defaultMessage="No activity in this period"
                            />
                        }
                        yLabel={intl.formatMessage(i18nMessages.courseStatisticsLessonsPassed)}
                    />

                    <Typography className={classes.sectionTitle}>
                        {intl.formatMessage(i18nMessages.courseStatisticsScoreDistribution)}
                    </Typography>
                    <Typography className={classes.caption}>
                        {intl.formatMessage(i18nMessages.courseStatisticsUsersWithScore, {
                            count: trend.usersWithScore,
                        })}
                    </Typography>

                    {trend.usersWithScore > 0 && (
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={distributionData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="users" fill={CapeeshColors.Primary600} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            )}
        </div>
    )
}

export default CourseStatsDetailPanel
